import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ProjectAssignmentHandler, AssignmentResult, ConfirmService, WorkflowService, effectiveStatus, MapMarker } from '@luzula/luzula-lib';
import { DataService } from '../../data.service';
import { ProjectService } from '../../project.service';
import { AuthService } from '../../auth.service';
import { restartWorkflow } from '@luzula/luzula-lib';
import { AufnahmePunktDocument } from '../../data-model';

/**
 * Handler für Projekt-Zuweisungsoperationen für AufnahmePunktDocuments.
 * Kapselt alle datenmodell- und service-spezifischen Zugriffe.
 */
@Injectable({ providedIn: 'root' })
export class BohrpunktProjectAssignmentHandler implements ProjectAssignmentHandler {
	private ds = inject(DataService);
	private ps = inject(ProjectService);
	private msg = inject(ToastrService);
	private confirmService = inject(ConfirmService);
	private workflow = inject(WorkflowService);
	private aus = inject(AuthService);

	/** Cache für zugeordnete Punkt-IDs (wird von MapComponent gesetzt) */
	private _assignedPointIds = new Set<string>();

	/**
	 * Setzt die IDs der zugeordneten Punkte.
	 * Wird von MapComponent aufgerufen wenn sich die Projektpunkte ändern.
	 */
	setAssignedPointIds(ids: Set<string>): void {
		this._assignedPointIds = ids;
	}

	/**
	 * Gibt die IDs aller dem aktiven Projekt zugeordneten Punkte zurück.
	 */
	getAssignedPointIds(): Set<string> {
		return this._assignedPointIds;
	}

	/**
	 * Einfachmodell (PLAN-single-membership): Die generische Mehrfach-Zuordnung
	 * entfällt — eine Bohrung gehört zu genau EINEM Projekt. Das Karten-
	 * Kontextmenü „Zuordnen"/„Entfernen" wird daher ausgeblendet (canAssign/
	 * canRemove → false). Eine bestehende Bohrung kommt nur noch über die
	 * Übernahme („In dieses Projekt übernehmen und zur Bearbeitung freigeben",
	 * PointFreigabeService) ins Projekt, die den Punkt umhängt. Lib bleibt
	 * unberührt (nur das App-Handler-Flag steuert die Sichtbarkeit).
	 */
	canAssign(): boolean {
		return false;
	}

	canRemove(_pointId: string): boolean {
		return false;
	}

	/**
	 * Per-Punkt-Lösch-Gate fuer die UI-Sichtbarkeit (Karten-Kontextmenue).
	 * Der `MapMarker.data` traegt das voll geladene `AufnahmePunktDocument`
	 * (gebaut in `BohrpunktMapMarkerHandler.toMapMarker`), sodass
	 * `ProjectService.canIRemovePoint` ohne weiteren Lookup entscheiden kann
	 * — symmetrisch zu Detail-Toolbar und Bulk-Button. Wenn das Doc fehlt,
	 * verweigern wir konservativ (Defense-in-Depth; der DB-Pfad in
	 * `deletePoint`/`deletePoints` erzwingt das Gate ohnehin nochmal).
	 */
	canDeletePoint(marker: MapMarker): boolean {
		const doc = marker?.data as AufnahmePunktDocument | undefined;
		if (!doc) return false;
		return this.ps.canIRemovePoint(doc);
	}

	/**
	 * Ordnet einen Punkt dem aktiven Projekt zu.
	 *
	 * Sonderfall: Hat der Punkt einen abgeschlossenen Workflow, wird vor dem
	 * Hinzufuegen ein Dialog mit drei Optionen angezeigt:
	 *  - "Neu starten": Workflow wird mit reset-Marker archiviert und neu
	 *     gestartet (Status -> bearbeitung, projectId = neues Projekt).
	 *  - "Nur zuordnen, kein Reset": Punkt landet in header.projects, der
	 *     bestehende Workflow bleibt unveraendert.
	 *  - "Abbrechen": keine Aenderung.
	 */
	async assignToProject(pointId: string): Promise<AssignmentResult> {
		const projectDoc = this.ps.currentProjectDoc();
		if (!projectDoc) {
			return { success: false, message: 'Kein Projekt aktiv' };
		}

		try {
			// Vollständiges Dokument aus DB laden
			const fullDoc = await firstValueFrom(this.ds.getDocument$(pointId)) as AufnahmePunktDocument;
			if (!fullDoc?.header) {
				return { success: false, message: 'Punkt nicht gefunden' };
			}

			// Projects-Array initialisieren falls nicht vorhanden
			if (!fullDoc.header.projects) {
				fullDoc.header.projects = [];
			}

			// Prüfen ob bereits zugeordnet
			if (fullDoc.header.projects.some(p => p.id === projectDoc._id)) {
				return { success: false, message: 'Punkt ist bereits dem Projekt zugeordnet' };
			}

			let didRestart = false;
			if (effectiveStatus(fullDoc) === 'abgeschlossen') {
				// Freigabe (Workflow-Reset beim Zuordnen eines abgeschlossenen Alt-/
				// Fremdpunkts) ist eine privilegierte Aktion (G6/P9): Manager (+
				// Entwickler-Bypass). Owner als reiner Bearbeiter darf das seit
				// 2026-05-26 nicht mehr — siehe canIfreigeben-Docstring.
				if (!this.ps.canIfreigeben()) {
					return {
						success: false,
						message: 'Abgeschlossene Punkte dürfen nur Manager zur Bearbeitung freigeben.'
					};
				}
				const choice = await this.confirmService.confirm({
					title: 'Workflow neu starten?',
					message:
						`Dieser Punkt ist im bisherigen Projekt abgeschlossen. ` +
						`Soll fuer "${projectDoc.name}" ein neuer Workflow gestartet werden? ` +
						`Die bisherige Historie bleibt erhalten.`,
					confirmText: 'Neu starten',
					confirmClass: 'btn-primary',
					extraText: 'Nur zuordnen, kein Reset',
					extraClass: 'btn-secondary',
					cancelText: 'Abbrechen'
				});
				if (choice === false) {
					return { success: false, message: 'Zuordnung abgebrochen' };
				}
				if (choice === true) {
					const userId = this.aus.currentUserId ?? '';
					restartWorkflow(fullDoc as any, {
						toProjectId: projectDoc._id,
						toProjectName: projectDoc.name,
						user: this.aus.currentUser,
						userId
					});
					didRestart = true;
				}
				// choice === 'extra': nur zuordnen, kein Reset — Workflow unveraendert
			}

			// Projekt hinzufügen
			fullDoc.header.projects.push({ id: projectDoc._id, name: projectDoc.name });
			await this.ds.saveDocument(fullDoc);

			// Cache aktualisieren
			this._assignedPointIds.add(pointId);

			this.msg.success(didRestart
				? 'Punkt zugeordnet und Workflow neu gestartet'
				: 'Punkt wurde dem Projekt zugeordnet');
			return { success: true };
		} catch (error) {
			console.error('Fehler bei Projektzuordnung:', error);
			return { success: false, message: 'Fehler bei der Projektzuordnung' };
		}
	}

	/**
	 * Entfernt einen Punkt aus dem aktiven Projekt.
	 */
	async removeFromProject(pointId: string): Promise<AssignmentResult> {
		const projectId = this.ps.project();
		if (!projectId) {
			return { success: false, message: 'Kein Projekt aktiv' };
		}

		try {
			// Vollständiges Dokument aus DB laden
			const fullDoc = await firstValueFrom(this.ds.getDocument$(pointId)) as AufnahmePunktDocument;
			if (!fullDoc?.header?.projects) {
				return { success: false, message: 'Punkt nicht gefunden oder keine Projekte' };
			}

			// Projekt entfernen
			fullDoc.header.projects = fullDoc.header.projects.filter(p => p.id !== projectId);
			await this.ds.saveDocument(fullDoc);

			// Cache aktualisieren
			this._assignedPointIds.delete(pointId);

			return { success: true };
		} catch (error) {
			console.error('Fehler beim Entfernen aus Projekt:', error);
			return { success: false, message: 'Fehler beim Entfernen aus dem Projekt' };
		}
	}

	/**
	 * Löscht einen einzelnen Punkt — mit Per-Punkt-Lösch-Gate (Matrix §4.2 B8).
	 * Das Detail-View gated bereits via `initButtons`; dieser Handler ist der
	 * gemeinsame Ein-Punkt-Pfad fuer das Karten-Kontextmenue und muss daselbe
	 * Gate vorhalten. Dokument einmal laden, `ProjectService.canIRemovePoint`
	 * fragen, sonst verweigern (kein DB-Aufruf).
	 */
	async deletePoint(pointId: string): Promise<AssignmentResult> {
		try {
			const doc = await firstValueFrom(this.ds.getDocument$(pointId)) as AufnahmePunktDocument | undefined;
			if (!this.ps.canIRemovePoint(doc as any)) {
				this.msg.warning('Keine Berechtigung, diesen Punkt zu löschen.');
				return { success: false, message: 'Keine Löschberechtigung' };
			}
			const response = await this.ds.removeBohrpunkt(pointId);
			if (response?.ok && response?.id) {
				return { success: true };
			}
			return { success: false, message: 'Punkt konnte nicht gelöscht werden' };
		} catch (error) {
			console.error('Fehler beim Löschen:', error);
			return { success: false, message: 'Fehler beim Löschen des Punktes' };
		}
	}

	/**
	 * Loescht mehrere Punkte — mit Per-Punkt-Lösch-Gate (Matrix §4.2 B8).
	 * Pendant zu `alle-bohrpunkte.deleteSelection`: pro Punkt prueft
	 * `ProjectService.canIRemovePoint`, fremde/eingefrorene/Kontroll-Punkte
	 * werden uebersprungen und gemeldet, niemals stillschweigend geloescht.
	 */
	async deletePoints(pointIds: string[]): Promise<AssignmentResult> {
		try {
			const docs = await this.ds.bulkGet(pointIds);
			const allowed: string[] = [];
			for (const doc of docs) {
				if (this.ps.canIRemovePoint(doc as any)) {
					allowed.push(doc._id);
				}
			}
			const skipped = pointIds.length - allowed.length;
			if (allowed.length === 0) {
				this.msg.warning('Keiner der ausgewählten Punkte darf gelöscht werden (nur eigene Punkte in Bearbeitung).');
				return { success: false, message: 'Keine Löschberechtigung' };
			}
			await this.ds.removeBohrpunkteBulk(allowed);
			let resultMsg = `${allowed.length} Punkt${allowed.length > 1 ? 'e' : ''} gelöscht`;
			if (skipped > 0) {
				resultMsg += ` · ${skipped} übersprungen (keine Löschrechte)`;
				this.msg.warning(`${skipped} Punkt${skipped > 1 ? 'e' : ''} übersprungen (keine Löschrechte)`);
			}
			this.msg.success(resultMsg);
			return { success: true };
		} catch (error) {
			console.error('Fehler beim Bulk-Löschen:', error);
			return { success: false, message: 'Fehler beim Löschen der Punkte' };
		}
	}
}
